import React from 'react';
import './PhotoDisplay.css';
import { Link, withRouter, RouteComponentProps } from 'react-router-dom';
import { GalleryImage } from 'components/Main/PhotoSection/Gallery.interface';
import { environments } from 'environments/environments';
import { Swipeable } from "react-swipeable";

interface Props { activePhoto: number, photoList: GalleryImage[], galleryName?: string, close?: any };
interface State { [key: string]: any }

const LEFT = +1;
const RIGHT = -1;

class PhotoDisplay extends React.Component<Props & RouteComponentProps, State> {
  state = {
    displayRatio: 1,
    activePhoto: this.props.activePhoto,
    anticache: Date.now()
  }

  /** Subscribe to window resize event on init */
  componentDidMount() {
    window.onresize = this.windowResize.bind(this);
    window.onkeydown = this.windowKeyPress.bind(this);
  }

  /** Unsubscribe from window resize event on close */
  componentWillUnmount() {
    window.onresize = null;
    window.onkeydown = null;
  }

  /** This state is only here to run rerender, displayRatio is actually never used. */
  windowResize() {
    this.setState({ displayRatio: this.getImageToWindowRatio() });
  }

  /** Checks what key was pressed and swipes if it was arrow or closes when escape.
   * @param key KeyboardEvent with all info about pressed key.
   */
  windowKeyPress(key: KeyboardEvent) {
    switch (key.keyCode) {
      case 37: this.onSideSwipe(RIGHT); break; // Left key means right swipe
      case 39: this.onSideSwipe(LEFT); break; // Right key means left swipe
      case 27: this.onDownSwipe(); break; // Escape means down swipe
      default: return;
    }
  }

  /** Returns ratio of active photo to current screen size for it to fit. */
  getImageToWindowRatio() {
    const activePhoto = this.props.photoList.find(image => +image.id === +this.props.activePhoto);
    if (!activePhoto) { return 1 }

    const windowWidth = window.innerWidth * 0.9;
    const windowHeight = window.innerHeight * 0.9;
    const imageWidth = activePhoto.width;
    const imageHeight = activePhoto.height;
    const widthRatio = windowWidth / imageWidth;
    const heightRatio = windowHeight / imageHeight;
    return Math.min(widthRatio, heightRatio, 1);
  }

  /** Runs after photoswipe to sides, changes photo to previous or next photo accordingly
   * @param direction Direction increment (left swipe adds 1, right subtracts)
   */
  onSideSwipe(direction: 1 | -1) {
    this.setState({ anticache: Date.now() })
    const photoList = this.props.photoList;
    const activePhotoIndex = photoList.findIndex(image => +image.id === +(this.props.close ? this.state.activePhoto : this.props.activePhoto));
    if (activePhotoIndex === -1) { return; }
    let newPhotoIndex: number;
    if (activePhotoIndex + direction >= photoList.length) {
      newPhotoIndex = 0;
    } else if (activePhotoIndex + direction < 0) {
      newPhotoIndex = photoList.length - 1;
    } else {
      newPhotoIndex = activePhotoIndex + direction;
    }
    const newPhoto = photoList[newPhotoIndex];
    if (!this.props.close) {
      this.props.history.push(`./${newPhoto.id}-${newPhoto.filename.replace(/\//g, '')}`);
    } else {
      this.setState({ activePhoto: newPhotoIndex });
    }
  }

  /** Closes photo by erasing it from route. */
  onDownSwipe() {
    if (!this.props.close) {
      this.props.history.push('./');
    } else {
      this.close();
    }
  }

  close() {
    if (this.props.close) {
      this.props.close();
    }
  }

  render() {
    const photoList = this.props.photoList;
    const activePhoto = photoList.find(image => +image.id === +(this.props.close ? this.state.activePhoto : this.props.activePhoto));
    const activePhotoIndex = photoList.findIndex(image => +image.id === +(this.props.close ? this.state.activePhoto : this.props.activePhoto));
    const ratio = this.getImageToWindowRatio();
    let smallImageURL = activePhoto
      ? activePhoto.filename.includes('http')
        ? activePhoto.filename
        : `${environments.server}/img/${activePhoto.thumbnail || activePhoto.filename}`
      : `${environments.server}/img/DT_bubak.png`;
    // console.log(activePhoto, smallImageURL, this.props.close);
    const style = activePhoto
      ? {
        backgroundImage: this.props.close
          ? `url(${activePhoto.filename})`
          : `url(${smallImageURL})`,
        maxWidth: `${activePhoto.width * ratio}px`,
        maxHeight: `${activePhoto.height * ratio}px`,
        minWidth: `${activePhoto.width * ratio}px`,
        minHeight: `${activePhoto.height * ratio}px`,
      }
      : {
        backgroundImage: `url(${smallImageURL})`,
        width: '360px',
        height: '450px',
      };
    const imgUrl = activePhoto
      ? activePhoto.filename.includes('http')
        ? activePhoto.filename
        : activePhoto.filename.includes('/img/')
          ? `${environments.server}/${activePhoto.filename}`
          : `${environments.server}/img/${activePhoto.filename}`
      : '';

    return (
      <div className="section">
        {this.props.close
          ? <div className="photo-display-overlay photo-display-overlay-closable" onClick={() => this.close()}></div>
          : <Link to='./' className="section">
            <div className="photo-display-overlay photo-display-overlay-closable"></div>
          </Link>
        }
        <div className="photo-display-overlay">
          <div className="photo-display-photo" style={style}>
            {imgUrl
              ? <img src={imgUrl || smallImageURL} alt='' />
              : null
            }
            <Swipeable
              trackMouse
              preventDefaultTouchmoveEvent
              onSwipedLeft={() => this.onSideSwipe(LEFT)}
              onSwipedRight={() => this.onSideSwipe(RIGHT)}
              onSwipedUp={() => this.onDownSwipe()}
            >
              <div className="photo-display-photo-overlay"></div>
            </Swipeable>
          </div>
          {this.props.photoList.length > 1
            ? (<span className="photo-display-number">
              {activePhotoIndex + 1} / {photoList.length}
              {this.props.galleryName
                ? <span className="photo-display-gallery"> - {this.props.galleryName}</span>
                : null
              }
            </span>)
            : null
          }
          {this.props.photoList.length > 1
            ? (<div className="photo-display-arrow" onClick={() => this.onSideSwipe(RIGHT)}>
              &lt;
            </div>)
            : null
          }
          {this.props.photoList.length > 1
            ? (<div className="photo-display-arrow photo-display-arrow-right" onClick={() => this.onSideSwipe(LEFT)}>
              &gt;
            </div>)
            : null
          }
          {this.props.close
            ? <span className="photo-display-close" onClick={() => this.close()}>×</span>
            : <Link to='./' className="photo-display-close">×</Link>
          }
        </div>
      </div>
    );
  }
}

export default withRouter(PhotoDisplay)